import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { FONT } from "../../styles/brandTokens";
import DirectoryUserPicker from "./DirectoryUserPicker";
import { CustomerApprovers, DirectorySource } from "./customerApprovers";

interface CustomerApproversFormProps {
  customerId: string;
  customerName: string;
  directory: DirectorySource;
  approvers: CustomerApprovers;
  onChange: (approvers: CustomerApprovers) => void;
}

type DirectoryStatus = "checking" | "connected" | "not_connected" | "error";

export default function CustomerApproversForm({ customerId, customerName, directory, approvers, onChange }: CustomerApproversFormProps) {
  const [status, setStatus] = useState<DirectoryStatus>("checking");

  useEffect(() => {
    let isActive = true;
    setStatus("checking");
    directory
      .hasConnectedDirectory(customerId)
      .then((isConnected) => {
        if (isActive) setStatus(isConnected ? "connected" : "not_connected");
      })
      .catch(() => {
        if (isActive) setStatus("error");
      });
    return () => {
      isActive = false;
    };
  }, [customerId, directory]);

  if (status === "checking") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} aria-label="Checking the customer's directory" />
      </Box>
    );
  }

  if (status !== "connected") {
    return (
      <Alert severity={status === "error" ? "error" : "info"} sx={{ fontFamily: FONT.sans }}>
        {status === "error"
          ? `Couldn't check ${customerName}'s directory connection. Try again in a moment.`
          : `Connect Microsoft 365 or Google Workspace for ${customerName} to pick approvers from their directory.`}
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      <DirectoryUserPicker
        label="Customer admins"
        helperText="Can authorize work on behalf of their coworkers."
        customerId={customerId}
        directory={directory}
        value={approvers.admins}
        onChange={(admins) => onChange({ ...approvers, admins })}
      />
      <DirectoryUserPicker
        label="HR contacts"
        helperText="Confirm hires, departures, and leave before lifecycle changes start."
        customerId={customerId}
        directory={directory}
        value={approvers.hrContacts}
        onChange={(hrContacts) => onChange({ ...approvers, hrContacts })}
      />
    </Stack>
  );
}
